// localStorage helpers for client-only UI preferences. Each function is
// guarded for SSR / restricted-storage environments where `window` or
// `localStorage` may be unavailable.

const hasStorage = (): boolean => {
    try {
        return typeof window !== 'undefined' && !!window.localStorage;
    } catch {
        return false;
    }
};

export function readString(key: string, fallback: string): string {
    if (!hasStorage()) return fallback;
    try {
        const v = window.localStorage.getItem(key);
        return v ?? fallback;
    } catch {
        return fallback;
    }
}

export function writeString(key: string, value: string): void {
    if (!hasStorage()) return;
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // ignore quota / privacy-mode errors
    }
}

export function readBool(key: string, fallback: boolean): boolean {
    const raw = readString(key, '');
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return fallback;
}

export function writeBool(key: string, value: boolean): void {
    writeString(key, value ? 'true' : 'false');
}

export function readJSON<T>(key: string, fallback: T): T {
    const raw = readString(key, '');
    if (!raw) return fallback;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

export function writeJSON<T>(key: string, value: T): void {
    try {
        writeString(key, JSON.stringify(value));
    } catch {
        // ignore serialization errors
    }
}

export function removeKey(key: string): void {
    if (!hasStorage()) return;
    try {
        window.localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

// Storage keys (namespaced to avoid collisions with future preferences).
export const STORAGE_KEYS = {
    homeFavoritesOnly: 'crapapp.homeFavoritesOnly',
    casinoSearchText: 'crapapp.casinoSearchText',
    casinoSortFieldType: 'crapapp.casinoSortFieldType',
    authUser: 'crapapp.authUser',
    ageVerified: 'crapapp.ageVerified',
} as const;
